/**
 * Topic normalization for study filtering.
 *
 * Questions store a single subject/topic/subtopic in payload JSONB.
 * Raw topics from AI generation are often messy (ALL CAPS, prefixes like
 * "Q1 - ", suffixes like "- NAXLEX NCLEX", etc.). This module maps every
 * raw topic to one or more CANONICAL category names so the topic picker
 * shows clean, standardized categories.
 */

// ── Canonical categories ─────────────────────────────────────────────────────
// Every question maps to one or more of these. Nothing else should appear
// in the topic picker.

const CANONICAL_CATEGORIES = new Set([
  'Cardiovascular',
  'Endocrine',
  'Fluid & Electrolytes',
  'Gastrointestinal',
  'Hematology',
  'Hepatic',
  'Immunology',
  'Infectious Disease',
  'Integumentary',
  'Musculoskeletal',
  'Neurological',
  'Nutrition',
  'Oncology',
  'Pediatrics',
  'Pharmacology',
  'Psychiatric/Mental Health',
  'Renal',
  'Reproductive',
  'Respiratory',
]);

// ── Keyword → canonical mapping ──────────────────────────────────────────────
// Lowercase keyword/phrase → canonical category name. Checked against the
// cleaned topic string after stripping prefixes/suffixes.

const KEYWORD_MAP: [RegExp, string][] = [
  // Cardiovascular
  [/\b(cardiovascular|cardiac|heart|coronary|hypertension|htn|chf|dvt|pe\b|arrhythmi|atrial|ventricular|angina|myocardial|aortic|valvular|ecg|ekg|hemodynamic)/i, 'Cardiovascular'],
  // Endocrine
  [/\b(endocrine|thyroid|diabetes|diabetic|dka|hhs|glycemic|insulin|adrenal|pituitary|cushing|addison|hormone|parathyroid|hypothyroid|hyperthyroid|a1c|glucose)/i, 'Endocrine'],
  // Fluid & Electrolytes
  [/\b(fluid|electrolyte|sodium|potassium|calcium|magnesium|dehydrat|overhydrat|hyponatremia|hypernatremia|hypokalemia|hyperkalemia|acid.base|acidosis|alkalosis|iv fluid)/i, 'Fluid & Electrolytes'],
  // Gastrointestinal
  [/\b(gastrointestinal|gastro|gi\b|bowel|colon|stomach|intestin|esophag|gerd|ulcer|crohn|colitis|diverticu|appendic|cholecyst|gallbladder|pancreat|nausea|vomit|diarrhea|constipat|obstruct|ileus|hernia|gastritis|hepatitis)/i, 'Gastrointestinal'],
  // Hematology
  [/\b(hematolog|anemia|sickle.cell|leukemia|lymphoma|platelet|thrombocyt|coagulat|bleeding|transfus|hemophilia|neutropeni|pancytopeni|wbc|rbc|hemoglobin|hematocrit)/i, 'Hematology'],
  // Hepatic
  [/\b(hepatic|liver|cirrhosis|hepatitis|portal hypertension|ascites|jaundice|bilirubin|hepato)/i, 'Hepatic'],
  // Immunology
  [/\b(immun|autoimmun|allerg|anaphyla|lupus|rheumatoid|hiv|aids|immunodeficien|immunosuppress|transplant reject)/i, 'Immunology'],
  // Infectious Disease
  [/\b(infect|sepsis|septic|mrsa|uti|pneumonia|tuberculosis|tb\b|meningitis|cellulitis|wound infect|antibiotic|antimicrobial|microbio)/i, 'Infectious Disease'],
  // Integumentary
  [/\b(integumentary|skin|wound|burn|pressure.ulcer|pressure.injur|dermat|rash|lesion|melanoma|psoriasis|eczema)/i, 'Integumentary'],
  // Musculoskeletal
  [/\b(musculoskeletal|msk|orthopedic|fracture|osteoporosis|arthritis|joint|mobility|amputation|cast|traction|sprain|strain|gout|osteomyelitis)/i, 'Musculoskeletal'],
  // Neurological
  [/\b(neuro|brain|stroke|cva|seizure|epilepsy|parkinson|alzheimer|dementia|multiple.sclerosis|spinal.cord|traumatic.brain|tbi|cranial|meningit|encephalit|neuropath|conscious|glasgow|icp|intracranial)/i, 'Neurological'],
  // Nutrition
  [/\b(nutrition|diet|malnutrit|obesity|bmi|feeding|enteral|parenteral|tpn|vitamin|mineral|calorie|protein.intake)/i, 'Nutrition'],
  // Oncology
  [/\b(oncolog|cancer|tumor|malignan|chemotherapy|radiation|carcinoma|metasta|biopsy|staging)/i, 'Oncology'],
  // Pediatrics
  [/\b(pediatric|peds|child|infant|neonat|newborn|toddler|adolescen|immunization|growth.develop|failure.to.thrive)/i, 'Pediatrics'],
  // Pharmacology
  [/\b(pharmacolog|pharm|medication|drug|dosage|dose|adverse.effect|side.effect|contraindic|drug.interact|antihypertensive|analgesic|opioid|nsaid|anticoagulant|antidiabetic|antibiotic|benzodiazepine|ssri|ace.inhibitor|beta.blocker|diuretic|statin)/i, 'Pharmacology'],
  // Psychiatric / Mental Health
  [/\b(psychiatr|mental.health|psych|depression|anxiety|bipolar|schizophren|suicide|substance.abuse|alcohol|withdrawal|eating.disorder|ptsd|personality.disorder|therapeutic.commun)/i, 'Psychiatric/Mental Health'],
  // Renal
  [/\b(renal|kidney|gu\b|genitourin|nephro|dialysis|hemodialysis|peritoneal|ckd|aki|acute.kidney|chronic.kidney|urinary|bladder|prostate|bph|catheter|urostomy|nephrolith|kidney.stone|creatinine|bun\b|glomerulo)/i, 'Renal'],
  // Reproductive
  [/\b(reproduct|obstetric|ob\b|pregnan|labor|delivery|postpartum|prenatal|antepartum|fetal|cesarean|c.section|preeclampsia|eclampsia|placenta|breast|gynecolog|menstrua|contracepti|fertility|std\b|sti\b)/i, 'Reproductive'],
  // Respiratory
  [/\b(respiratory|pulmonary|lung|asthma|copd|pneumo|bronch|oxygen|ventilat|intubat|tracheostomy|chest.tube|ards|dyspnea|cough|airway|suctioning|spirometry|peak.flow|pleural|pulmonary.embol)/i, 'Respiratory'],
];

// Topics that are too generic to be useful as standalone categories
const GENERIC_TOPICS = /^(nursing|general nursing|medicine|health|science|general|nclex|nclex-rn|nclex-pn|naxlex|ati|ati practice|hesi|review|practice|study|test|exam|gmat|gre|lsat|teas|cpa|cpa exam|pance|ancc|aanp|usmle|usmle step 1|usmle step 2 ck|bar-ube|uniform bar exam|assessment|diagnosis|planning|implementation|evaluation|uncategorized)$/i;

// Junk prefixes/suffixes to strip before keyword matching
const STRIP_PREFIX = /^(Q\d*\s*[-–—]\s*|NAXLEX\s*(EXPANSION\s*)?[-–—]\s*|ATI\s*(Practice\s*)?[-–—]\s*)/i;
const STRIP_SUFFIX = /\s*[-–—]\s*(NAXLEX\s*(NCLEX)?|NEW\s+SATA\s+QUESTIONS?|SATA\s+QUESTIONS?|SATA|NCLEX|ATI\s*Practice|ATI|HESI|Review|Practice)\s*$/i;

/**
 * Derive canonical category tags from a question's payload fields.
 * Returns a deduplicated array of standardized category strings.
 */
export function deriveCategories(payload: { subject?: string; topic?: string; subtopic?: string; private_to_user_id?: string }): string[] {
  // A question private to one user is a deliberately named personal study set
  // ("final", "OBGYN4"), not raw noisy AI output that needs system-based
  // cleanup, so show it under its own name instead of collapsing it into the
  // shared NCLEX categories below (subject "Maternal-Newborn Nursing" alone
  // would otherwise match Pediatrics via the word "newborn").
  if (payload.private_to_user_id && payload.topic) return [payload.topic];

  const fields = [payload.topic, payload.subtopic, payload.subject].filter(Boolean) as string[];

  if (fields.length === 0) return ['Uncategorized'];

  // Clean each field: strip prefixes, suffixes, normalize whitespace
  const cleaned = fields.map(f =>
    f.replace(STRIP_PREFIX, '').replace(STRIP_SUFFIX, '').trim()
  ).filter(f => f.length > 0);

  // NSG245: group by test_label first ("Test 3", "Test 3 ADPIE"), then
  // show the organ-system topic as a secondary category so students can
  // filter by both test and body system.
  if ((payload.subject || '').trim().toUpperCase() === 'NSG245') {
    const testLabel = ((payload as any).test_label || '').trim();
    const topic = (payload.topic || '').replace(STRIP_PREFIX, '').replace(STRIP_SUFFIX, '').trim();
    const tags: string[] = [];
    if (testLabel) tags.push(testLabel);
    if (topic && !GENERIC_TOPICS.test(topic)) tags.push(topic);
    return Array.from(new Set(tags.length ? tags : ['NSG245']));
  }

  const combined = cleaned.join(' ');
  const examTags = deriveExamPrepCategories(cleaned, combined);
  if (examTags.length > 0) return examTags;

  const tags = new Set<string>();

  // Run keyword matchers against the combined cleaned text
  for (const [pattern, category] of KEYWORD_MAP) {
    if (pattern.test(combined)) {
      tags.add(category);
    }
  }

  // If no canonical match found, use the cleaned primary topic as title case
  if (tags.size === 0) {
    const primary = cleaned[0] || 'Uncategorized';
    // Skip if it's a generic/junk topic
    if (GENERIC_TOPICS.test(primary)) {
      tags.add('General');
    } else {
      // Title-case it for consistency
      const titled = primary.replace(/\b\w/g, c => c.toUpperCase())
        .replace(/\b(And|Or|Of|The|In|For|To|A|An)\b/g, w => w.toLowerCase());
      tags.add(titled);
    }
  }

  return Array.from(tags);
}

function deriveExamPrepCategories(cleaned: string[], combined: string): string[] {
  const lower = combined.toLowerCase();
  const exam = detectExam(lower);
  if (!exam) return [];

  const candidates = cleaned.filter((field) => !GENERIC_TOPICS.test(field));
  const topicText = candidates.join(' ') || combined;

  const mappings: Record<string, [RegExp, string][]> = {
    GMAT: [
      [/\bdata sufficiency\b/i, 'Data Sufficiency'],
      [/\btable analysis\b/i, 'Table Analysis'],
      [/\bgraphics? interpretation\b/i, 'Graphics Interpretation'],
      [/\bmulti.source\b/i, 'Multi-Source Reasoning'],
      [/\btwo.part\b/i, 'Two-Part Analysis'],
      [/\bcritical reasoning\b/i, 'Critical Reasoning'],
      [/\breading comprehension\b/i, 'Reading Comprehension'],
      [/\bnumber propert|prime|divisib|integer|remainder\b/i, 'Number Properties'],
      [/\brate|work|mixture|interest|ratio|percent|proportion\b/i, 'Rates, Ratios & Percentages'],
      [/\bstatistic|probability|mean|median|average|data\b/i, 'Statistics & Probability'],
      [/\bgeometry|triangle|circle|coordinate\b/i, 'Geometry'],
      [/\balgebra|equation|inequal|function|expression\b/i, 'Algebra'],
      [/\barithmetic|fraction|decimal\b/i, 'Arithmetic'],
    ],
    GRE: [
      [/\btext completion\b/i, 'Text Completion'],
      [/\bsentence equivalence\b/i, 'Sentence Equivalence'],
      [/\breading comprehension\b/i, 'Reading Comprehension'],
      [/\bvocabulary|word meaning\b/i, 'Vocabulary in Context'],
      [/\bargument|assumption|evidence|critique\b/i, 'Argument Analysis'],
      [/\bissue|organization|essay\b/i, 'Issue Analysis'],
      [/\bdata|statistic|probability|mean|median\b/i, 'Data Analysis'],
      [/\bgeometry|triangle|circle|coordinate\b/i, 'Geometry'],
      [/\balgebra|equation|inequal|function\b/i, 'Algebra'],
      [/\barithmetic|number|integer|ratio|percent\b/i, 'Arithmetic'],
    ],
    LSAT: [
      [/\bflaw\b/i, 'Flaw'],
      [/\bstrengthen\b/i, 'Strengthen'],
      [/\bweaken\b/i, 'Weaken'],
      [/\bnecessary assumption\b/i, 'Necessary Assumption'],
      [/\bsufficient assumption\b/i, 'Sufficient Assumption'],
      [/\bassumption\b/i, 'Assumption'],
      [/\binference|must be true\b/i, 'Inference'],
      [/\bparallel\b/i, 'Parallel Reasoning'],
      [/\bprinciple\b/i, 'Principle'],
      [/\bresolve|paradox\b/i, 'Resolve the Paradox'],
      [/\bmain idea|author|tone|structure|passage\b/i, 'Reading Comprehension'],
      [/\blogical reasoning\b/i, 'Logical Reasoning'],
    ],
    TEAS: [
      [/\bmain idea|key ideas?|inference|evidence|text structure|craft|reading\b/i, 'Reading'],
      [/\balgebra|equation|proportion|percent|measurement|geometry|mean|data|mathematics|math\b/i, 'Mathematics'],
      [/\banatomy|physiology|life science|physical science|scientific reasoning|biology|chemistry|science\b/i, 'Science'],
      [/\bgrammar|punctuation|sentence|word meaning|vocabulary|spelling|language|english\b/i, 'English & Language Usage'],
    ],
    CPA: [
      [/\baudit evidence|substantive|sampling|confirmation|inventory observation\b/i, 'Audit Evidence'],
      [/\binternal control|coso|control deficiencies\b/i, 'Internal Controls'],
      [/\baudit report|opinion|going concern|emphasis\b/i, 'Audit Reports'],
      [/\bprofessional responsibil|ethics|independence\b/i, 'Professional Responsibilities'],
      [/\brevenue\b/i, 'Revenue Recognition'],
      [/\blease\b/i, 'Leases'],
      [/\bcash flow\b/i, 'Statement of Cash Flows'],
      [/\bgovernmental\b/i, 'Governmental Accounting'],
      [/\bindividual tax|taxation|deduction|credit\b/i, 'Individual Taxation'],
      [/\bentity tax|corporation|partnership\b/i, 'Entity Taxation'],
      [/\bbusiness law|ucc|contract\b/i, 'Business Law'],
      [/\bgovernance|audit committee\b/i, 'Corporate Governance'],
      [/\bcost accounting|overhead|variance\b/i, 'Cost Accounting'],
      [/\binformation technology|it audit|access control\b/i, 'Information Technology'],
      [/\beconomic|elastic|demand|supply\b/i, 'Economics'],
    ],
    UBE: [
      [/\bcivil procedure\b/i, 'Civil Procedure'],
      [/\bconstitutional law\b/i, 'Constitutional Law'],
      [/\bcontract|ucc\b/i, 'Contracts'],
      [/\bcriminal procedure\b/i, 'Criminal Procedure'],
      [/\bcriminal law\b/i, 'Criminal Law'],
      [/\bevidence\b/i, 'Evidence'],
      [/\breal property|property\b/i, 'Real Property'],
      [/\btort\b/i, 'Torts'],
      [/\bessay|mee\b/i, 'Essay Analysis'],
      [/\bperformance test|mpt|task memo|file|library\b/i, 'Performance Test'],
    ],
  };

  const tags = new Set<string>();
  for (const [pattern, category] of mappings[exam] ?? []) {
    if (pattern.test(topicText)) tags.add(category);
  }
  if (tags.size > 0) return Array.from(tags);

  const fallback = candidates.find((field) => !isExamSection(field)) || candidates[0];
  return fallback ? [toTitle(fallback)] : [];
}

function detectExam(lower: string): 'GMAT' | 'GRE' | 'LSAT' | 'TEAS' | 'CPA' | 'UBE' | null {
  if (/\bgmat\b/.test(lower)) return 'GMAT';
  if (/\bgre\b/.test(lower)) return 'GRE';
  if (/\blsat\b/.test(lower)) return 'LSAT';
  if (/\bteas\b/.test(lower)) return 'TEAS';
  if (/\bcpa\b/.test(lower)) return 'CPA';
  if (/\bbar-ube\b|\buniform bar exam\b|\bmultistate bar exam\b|\bmultistate essay exam\b|\bmultistate performance test\b/.test(lower)) return 'UBE';
  return null;
}

function isExamSection(value: string): boolean {
  return /^(quantitative reasoning|verbal reasoning|data insights|analytical writing|logical reasoning|reading comprehension|reading|mathematics|science|english and language usage|auditing and attestation|financial accounting and reporting|regulation|business environment & concepts|multistate bar exam|multistate essay exam|multistate performance test)$/i.test(value);
}

function toTitle(value: string): string {
  return value.replace(/\b\w/g, c => c.toUpperCase())
    .replace(/\b(And|Or|Of|The|In|For|To|A|An)\b/g, w => w.toLowerCase());
}

/**
 * Build a category -> Set<questionId> index from a list of questions.
 * Used by the topic picker to show categories with accurate counts.
 */
export function buildCategoryIndex(
  questions: { id: string; payload: any }[],
): Map<string, Set<string>> {
  const index = new Map<string, Set<string>>();

  for (const q of questions) {
    const cats = deriveCategories(q.payload);
    for (const cat of cats) {
      let set = index.get(cat);
      if (!set) {
        set = new Set();
        index.set(cat, set);
      }
      set.add(q.id);
    }
  }

  return index;
}
